import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/AuthContext'

export default function AcceptInvitePage() {
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [fullName, setFullName] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)
  const [workshopName, setWorkshopName] = useState('')
  const [validSession, setValidSession] = useState(false)
  const navigate = useNavigate()
  const { refreshWorkshop, profile } = useAuth()

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        setValidSession(true)
        setWorkshopName(session.user?.user_metadata?.workshop_name || 'your workshop')
      }
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if ((event === 'SIGNED_IN' || event === 'USER_UPDATED') && session) {
        setValidSession(true)
        setWorkshopName(session.user?.user_metadata?.workshop_name || 'your workshop')
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  async function handleAccept(e) {
    e.preventDefault()
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return }
    if (password !== confirm) { setError('Passwords do not match.'); return }
    setLoading(true)
    setError(null)

    // Set their password and name
    const { error: updateErr } = await supabase.auth.updateUser({
      password,
      data: { full_name: fullName }
    })

    if (updateErr) { setError(updateErr.message); setLoading(false); return }

    if (!profile) {
      const { error: inviteError } = await supabase.rpc('accept_workshop_invitation')
      if (inviteError) { setError(inviteError.message); setLoading(false); return }
    }

    // Update their profile name
    const { data: { session } } = await supabase.auth.getSession()
    if (session) {
      await supabase.from('profiles').update({ full_name: fullName }).eq('id', session.user.id)
    }

    setLoading(false)
    await refreshWorkshop()
    setSuccess(true)
  }

  if (success) {
    return (
      <div className="auth-wrap">
        <div className="auth-card">
          <div className="auth-logo">Powered by <span>Rebound</span></div>
          <div className="alert alert-success" style={{ marginBottom: 20 }}>
            You're in. Welcome to {workshopName}.
          </div>
          <p className="text-muted fs12" style={{ marginBottom: 18 }}>
            The dashboard will show your launch checklist so you can finish workshop details, add services, and share your booking widget.
          </p>
          <button className="btn btn-primary btn-full" onClick={() => navigate('/')}>
            Go to Dashboard
          </button>
        </div>
      </div>
    )
  }

  if (!validSession) {
    return (
      <div className="auth-wrap">
        <div className="auth-card">
          <div className="auth-logo">Powered by <span>Rebound</span></div>
          <p className="text-muted" style={{ textAlign: 'center', marginBottom: 16 }}>
            Validating invite link…
          </p>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <span className="spinner" style={{ width: 20, height: 20 }} />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="auth-wrap">
      <div className="auth-card">
        <div className="auth-logo">Powered by <span>Rebound</span></div>
        <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 6 }}>You've been invited</div>
        <p className="text-muted fs12" style={{ marginBottom: 20 }}>
          You've been added to <strong style={{ color: 'var(--light)' }}>{workshopName}</strong> on Rebound. Set a password to get started.
        </p>
        <div className="alert alert-info" style={{ marginBottom: 16 }}>
          <div className="fs12">Next: complete the launch checklist, add the workshop service menu, then share the booking widget.</div>
        </div>
        {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}
        <form onSubmit={handleAccept}>
          <div className="field">
            <label>Your Name</label>
            <input value={fullName} onChange={e => setFullName(e.target.value)} placeholder="e.g. Matt Calvino" required />
          </div>
          <div className="field">
            <label>Password</label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Min. 8 characters" required minLength={8} />
          </div>
          <div className="field" style={{ marginBottom: 20 }}>
            <label>Confirm Password</label>
            <input type="password" value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="Repeat password" required />
          </div>
          <button className="btn btn-primary btn-full" type="submit" disabled={loading}>
            {loading ? <><span className="spinner" />&nbsp;Setting up…</> : 'Accept Invite & Set Password'}
          </button>
        </form>
      </div>
    </div>
  )
}
